import React, { useCallback, useState } from 'react';

import { Paper, Popover } from '@mui/material';
import type { TElement } from 'platejs';
import type { PlateEditor, PlateElementProps } from 'platejs/react';
import { PlateElement } from 'platejs/react';
import { focusEditor } from 'src/components/ai/chat/ChatPanel/AlineMarkdownEditor/utils/focus';
import { makeStyles } from 'tss-react/mui';

import { ReportColumnMentionMenu } from './ReportColumnMentionMenu';

const useStyles = makeStyles()(theme => ({
  popoverContainer: {
    borderRadius: theme.shape.borderRadius,
    boxSizing: 'border-box' as const,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: theme.spacing(1),
    maxHeight: 'unset',
    overflow: 'hidden',
    padding: theme.spacing(0.5, 0),
  },
}));

interface ReportColumnMentionPopoverProps {
  anchorEl: HTMLElement | null;
  editor: PlateEditor;
  onClose: () => void;
  open: boolean;
}

export function ReportColumnMentionPopover({
  anchorEl,
  editor,
  onClose,
  open,
}: ReportColumnMentionPopoverProps): JSX.Element {
  const { classes } = useStyles();

  return (
    <Popover
      disableEnforceFocus
      disableRestoreFocus
      anchorEl={anchorEl}
      anchorOrigin={{ horizontal: 'left', vertical: 'top' }}
      open={open}
      slotProps={{
        transition: {
          onExited: () => focusEditor(editor),
        },
      }}
      transformOrigin={{ horizontal: 'left', vertical: 'bottom' }}
      onClose={onClose}
    >
      <Paper className={classes.popoverContainer} elevation={8}>
        <ReportColumnMentionMenu editor={editor} onClose={onClose} />
      </Paper>
    </Popover>
  );
}

export function ReportColumnMentionInputElement(props: PlateElementProps<TElement>): JSX.Element {
  const { editor } = props;
  const [triggerEl, setTriggerEl] = useState<HTMLSpanElement | null>(null);

  const handleClose = useCallback(() => {
    editor.tf.removeNodes({ at: [], match: n => n.type === 'mention_input' });
    setTriggerEl(null);
    focusEditor(editor);
  }, [editor]);

  return (
    <PlateElement {...props} as="span">
      <span ref={setTriggerEl} contentEditable={false}>
        @
      </span>
      <ReportColumnMentionPopover
        anchorEl={triggerEl}
        editor={editor}
        open={Boolean(triggerEl)}
        onClose={handleClose}
      />
      {props.children}
    </PlateElement>
  );
}
